import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getExpensesCollectionRefs, getApplicationCurrentBorrower } from 'selectors';
import { useSelector } from 'react-redux';

export const ScheduleTableContext = createContext();

export function useScheduleTable() {
  return useContext(ScheduleTableContext);
}

export function ScheduleTableProvider({
  children,
  user,
  stateName,
  legalName,
  scheduleData,
  borrowers,
  ...otherProps
}) {
  const [contacts, setContacts] = useState([]);
  const [searchButtonClicked, setSearchButtonClicked] = useState(false);
  const [lastDataHash, setLastDataHash] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(true);
  const currentBorrower = useSelector(getApplicationCurrentBorrower);
  const currentBorrowerName = currentBorrower && currentBorrower.displayName;
  const refs = useSelector(getExpensesCollectionRefs);

  const stateOptions = refs.statesCollection ? refs.statesCollection.map((state) => ({
    value: state.value,
    label: state.label,
  })) : [];

  const contactExists = useCallback((newContact, existingContacts) => (
    existingContacts.some((existingContact) => {
      const sameFullName = (existingContact.fullName || '').trim() === (newContact.fullName || '').trim();
      const sameState = (existingContact.state || '').trim() === (newContact.state || '').trim();
      const sameLlc = (existingContact.llc || '').trim() === (newContact.llc || '').trim();

      return sameFullName && sameState && sameLlc;
    })
  ), []);

  useEffect(() => {
    if (contacts.length === 0 && currentBorrowerName) {
      const initialContact = {
        fullName: currentBorrowerName,
        state: stateName.label || stateName,
        llc: legalName,
      };
      setContacts([initialContact]);
    }

    if (borrowers && borrowers.length > 1) {
      const borrowerContacts = borrowers.map((borrower) => ({
        fullName: borrower.borrower || '',
        state: borrower.state || '',
        llc: borrower.llc || '',
        stateCode: '',
      }));
      const uniqueBorrowerContacts = borrowerContacts.filter((newContact) => (
        !contactExists(newContact, contacts)
      ));

      if (uniqueBorrowerContacts.length > 0) {
        setContacts((prev) => [...prev, ...uniqueBorrowerContacts]);
      }
    }
  }, [borrowers, currentBorrowerName]);

  const addContact = useCallback(() => {
    setContacts((prev) => [
      ...prev,
      { fullName: '', state: '', llc: '' },
    ]);
  }, []);

  const removeContact = useCallback((idx) => {
    if (idx === 0) return;
    setContacts((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateContact = useCallback((idx, field, value) => {
    setContacts((prev) => prev.map((item, i) => {
      if (i === idx) {
        return { ...item, [field]: value };
      }

      return item;
    }));
  }, []);

  const isContactValid = useCallback((contact) => {
    const hasFullName = contact.fullName && contact.fullName.trim().length > 0;
    const foundState = stateOptions.find((state) => state.value === contact.stateCode) || {};
    const state = contact.state || foundState.label || '';
    const hasState = state && state.trim().length > 0;
    const hasLlc = contact.llc && contact.llc.trim().length > 0;
    const hasStateAndLlc = hasState && hasLlc;
    const hasNameAndState = hasFullName && hasState;

    return hasStateAndLlc || hasNameAndState;
  }, []);

  const areAllContactsValid = useMemo(() => {
    if (contacts.length === 0) return false;

    return contacts.every((contact) => isContactValid(contact));
  }, [contacts, isContactValid]);

  const isSearchDisabled = useMemo(() => !areAllContactsValid, [areAllContactsValid]);

  const contextValue = {
    user,
    stateName,
    legalName,

    searchButtonClicked,
    setSearchButtonClicked,

    contacts,
    setContacts,
    addContact,
    removeContact,
    updateContact,

    isContactValid,
    areAllContactsValid,
    isSearchDisabled,
    lastDataHash,
    setLastDataHash,
    isSubmitted,
    setIsSubmitted,

    ...otherProps,
  };

  return (
    <ScheduleTableContext.Provider value={contextValue}>
      {children}
    </ScheduleTableContext.Provider>
  );
}

ScheduleTableProvider.propTypes = {
  children: PropTypes.node.isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  stateName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string,
    }),
  ]),
  legalName: PropTypes.string,
  scheduleData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  borrowers: PropTypes.arrayOf(PropTypes.shape({
    borrower: PropTypes.string,
    state: PropTypes.string,
    llc: PropTypes.string,
    length: PropTypes.number,
    slice: PropTypes.func,
  })),
};

ScheduleTableProvider.defaultProps = {
  user: null,
  stateName: '',
  legalName: '',
  scheduleData: null,
  borrowers: [],
};
